import React, { useEffect, useState, useCallback } from 'react';
import {
  getWorkingDays,
  updateWorkingDays,
  getDaySchedule,
  toggleSlot,
  blockPeriod,
  unblockDay,
} from '../services/availabilityApi';

const DAY_LABELS = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday',
  friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
};
const ORDERED_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const STATUS_STYLES = {
  AVAILABLE: { bg: '#f0fdf4', border: '#0e9f6e', color: '#065f46', cursor: 'pointer' },
  BOOKED:    { bg: '#dbeafe', border: '#bfdbfe', color: '#1e40af', cursor: 'default' },
  BLOCKED:   { bg: '#fee2e2', border: '#fca5a5', color: '#991b1b', cursor: 'pointer' },
};

const dateLabel = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * Right-hand "Slot Configuration" panel from the D2-schedule.html reference.
 * Props:
 *  - selectedDate: 'YYYY-MM-DD'
 *  - onChanged(): called after any mutation so the parent can refresh the calendar events
 */
const SlotConfigPanel = ({ selectedDate, onChanged = () => {} }) => {
  const [workingDays, setWorkingDays] = useState(null);
  const [daySchedule, setDaySchedule] = useState(null);
  const [loadingDay, setLoadingDay] = useState(true);
  const [savingDay, setSavingDay] = useState(null); // key of day being toggled
  const [togglingSlot, setTogglingSlot] = useState(null);
  const [error, setError] = useState(null);

  const [blockForm, setBlockForm] = useState({
    startDate: selectedDate,
    endDate: selectedDate,
    period: 'ALL_DAY',
    reason: '',
  });
  const [blocking, setBlocking] = useState(false);
  const [blockMsg, setBlockMsg] = useState(null);
  const [unblocking, setUnblocking] = useState(false);

  const loadWorkingDays = useCallback(async () => {
    try {
      const data = await getWorkingDays();
      setWorkingDays(data);
    } catch {
      setError('Could not load working days.');
    }
  }, []);

  const loadDaySchedule = useCallback(async (date) => {
    if (!date) return;
    setLoadingDay(true);
    setError(null);
    try {
      const data = await getDaySchedule(date);
      setDaySchedule(data);
    } catch {
      setError('Could not load slots for this date.');
    } finally {
      setLoadingDay(false);
    }
  }, []);

  useEffect(() => { loadWorkingDays(); }, [loadWorkingDays]);
  useEffect(() => {
    loadDaySchedule(selectedDate);
    setBlockForm((f) => ({ ...f, startDate: selectedDate, endDate: selectedDate }));
    setBlockMsg(null);
  }, [selectedDate, loadDaySchedule]);

  const handleDayToggle = async (key) => {
    if (!workingDays) return;
    const next = { ...workingDays, [key]: !workingDays[key] };
    setWorkingDays(next); // optimistic
    setSavingDay(key);
    try {
      const saved = await updateWorkingDays(next);
      setWorkingDays(saved);
      loadDaySchedule(selectedDate);
      onChanged();
    } catch {
      setWorkingDays(workingDays); // revert
      setError('Could not update working days.');
    } finally {
      setSavingDay(null);
    }
  };

  const handleSlotClick = async (slot) => {
    if (slot.status === 'BOOKED') return;
    setTogglingSlot(slot.time);
    setError(null);
    try {
      const updated = await toggleSlot(selectedDate, slot.time);
      setDaySchedule(updated);
      onChanged();
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not update that slot.');
    } finally {
      setTogglingSlot(null);
    }
  };

  const handleUnblockDay = async () => {
    setUnblocking(true);
    setError(null);
    try {
      const updated = await unblockDay(selectedDate);
      setDaySchedule(updated);
      onChanged();
    } catch {
      setError('Could not unblock this day.');
    } finally {
      setUnblocking(false);
    }
  };

  const handleBlockSubmit = async (e) => {
    e.preventDefault();
    setBlocking(true);
    setBlockMsg(null);
    setError(null);
    try {
      await blockPeriod(blockForm);
      setBlockMsg('Period blocked successfully.');
      setBlockForm((f) => ({ ...f, reason: '' }));
      loadDaySchedule(selectedDate);
      onChanged();
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not block this period.');
    } finally {
      setBlocking(false);
    }
  };

  const panelHeading = selectedDate
    ? `Slot Configuration — ${new Date(selectedDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
    : 'Slot Configuration';

  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #f3f4f6', padding: 20, fontFamily: 'DM Sans, sans-serif' }}>
      <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: 14.5, color: '#0f172a', marginBottom: 16 }}>
        ⚙ {panelHeading}
      </div>

      {error && (
        <div style={{ fontSize: 11.5, color: '#991b1b', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* Working day toggles */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
        {ORDERED_DAYS.map((key) => {
          const on = workingDays ? !!workingDays[key] : false;
          return (
            <div
              key={key}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', borderRadius: 10, border: `1.5px solid ${on ? '#0e9f6e' : '#e5e7eb'}`,
                background: on ? '#f0fdf4' : '#fff', opacity: savingDay === key ? 0.6 : 1,
              }}
            >
              <span style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>{DAY_LABELS[key]}</span>
              <span
                role="switch"
                aria-checked={on}
                onClick={() => savingDay === null && handleDayToggle(key)}
                style={{
                  width: 36, height: 20, borderRadius: 10, position: 'relative', cursor: 'pointer',
                  background: on ? '#0e9f6e' : '#e5e7eb', transition: 'background .2s', flexShrink: 0,
                }}
              >
                <span style={{
                  position: 'absolute', width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  top: 2, left: on ? 18 : 2, transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </span>
            </div>
          );
        })}
      </div>

      {/* Today's slots */}
      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
        {dateLabel(selectedDate)} Time Slots
      </div>

      {loadingDay ? (
        <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 14 }}>Loading…</div>
      ) : !daySchedule ? (
        <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 14 }}>Select a date to configure slots.</div>
      ) : (
        <>
          {!daySchedule.workingDay && (
            <div style={{ fontSize: 11, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px', marginBottom: 10 }}>
              This weekday is turned off — all slots are closed.
            </div>
          )}
          {daySchedule.fullDayBlocked && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              fontSize: 11, color: '#991b1b', background: '#fef2f2', border: '1px solid #fca5a5',
              borderRadius: 8, padding: '6px 10px', marginBottom: 10,
            }}>
              <span>Full day blocked{daySchedule.blockReason ? `: ${daySchedule.blockReason}` : ''}.</span>
              <button
                onClick={handleUnblockDay}
                disabled={unblocking}
                style={{
                  flexShrink: 0, border: 'none', background: '#991b1b', color: '#fff',
                  borderRadius: 6, padding: '3px 9px', fontSize: 10.5, fontWeight: 700,
                  cursor: unblocking ? 'default' : 'pointer', opacity: unblocking ? 0.6 : 1,
                }}
              >
                {unblocking ? 'Unblocking…' : 'Unblock day'}
              </button>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 12 }}>
            {daySchedule.slots.map((slot) => {
              const s = STATUS_STYLES[slot.status] || STATUS_STYLES.AVAILABLE;
              const busy = togglingSlot === slot.time;
              // While the whole day is blocked, individual slots can't be toggled
              // independently — the backend rejects it, so keep this consistent.
              const disabled = busy || (daySchedule.fullDayBlocked && slot.status !== 'BOOKED');
              return (
                <div
                  key={slot.time}
                  onClick={() => !disabled && handleSlotClick(slot)}
                  title={slot.status === 'BOOKED' ? slot.patientName : daySchedule.fullDayBlocked ? 'Unblock the day first' : slot.status}
                  style={{
                    padding: 7, borderRadius: 8, textAlign: 'center', fontSize: 11, fontWeight: 600,
                    border: `1.5px solid ${s.border}`, background: s.bg, color: s.color,
                    cursor: disabled ? 'default' : s.cursor,
                    opacity: busy ? 0.5 : disabled ? 0.7 : 1,
                  }}
                >
                  {slot.label}
                </div>
              );
            })}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 12, fontSize: 10.5, marginBottom: 16, flexWrap: 'wrap' }}>
        <LegendDot color="#f0fdf4" border="#0e9f6e" label="Available" />
        <LegendDot color="#dbeafe" border="#bfdbfe" label="Booked" />
        <LegendDot color="#fee2e2" border="#fca5a5" label="Blocked" />
      </div>

      {/* Block time / leave */}
      <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10 }}>🚫 Block Time / Leave</div>
        <form onSubmit={handleBlockSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            type="date"
            value={blockForm.startDate || ''}
            onChange={(e) => setBlockForm((f) => ({ ...f, startDate: e.target.value }))}
            required
            style={inputStyle}
          />
          <input
            type="date"
            value={blockForm.endDate || ''}
            onChange={(e) => setBlockForm((f) => ({ ...f, endDate: e.target.value }))}
            min={blockForm.startDate || undefined}
            required
            style={inputStyle}
          />
          <select
            value={blockForm.period}
            onChange={(e) => setBlockForm((f) => ({ ...f, period: e.target.value }))}
            style={inputStyle}
          >
            <option value="ALL_DAY">All Day</option>
            <option value="MORNING">Morning (9AM–12PM)</option>
            <option value="AFTERNOON">Afternoon (1PM–7:30PM)</option>
          </select>
          <textarea
            rows={2}
            placeholder="Reason (e.g. Conference, Leave, Personal)..."
            value={blockForm.reason}
            onChange={(e) => setBlockForm((f) => ({ ...f, reason: e.target.value }))}
            style={{ ...inputStyle, resize: 'none', fontFamily: 'inherit' }}
          />
          {blockMsg && <div style={{ fontSize: 11, color: '#059669' }}>{blockMsg}</div>}
          <button
            type="submit"
            disabled={blocking}
            style={{
              width: '100%', padding: 9, background: '#fee2e2', color: '#991b1b', border: 'none',
              borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: blocking ? 'default' : 'pointer',
              opacity: blocking ? 0.7 : 1, marginTop: 2,
            }}
          >
            {blocking ? 'Blocking…' : '🚫 Block This Period'}
          </button>
        </form>
      </div>
    </div>
  );
};

const inputStyle = {
  padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 12.5, outline: 'none', background: '#fff', width: '100%',
};

const LegendDot = ({ color, border, label }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#374151' }}>
    <span style={{ width: 9, height: 9, borderRadius: 2, background: color, border: `1px solid ${border}`, display: 'inline-block' }} />
    {label}
  </span>
);

export default SlotConfigPanel;